import React, { useEffect, useState, useCallback } from "react";
import { apiCall } from "../config/api";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
} from "@mui/icons-material";

// Log categories with colors
const categoryColors = {
  pin: "primary",
  user: "secondary",
  campaign: "success",
  notification: "warning",
  moderation: "error",
  system: "default",
};

// Log actions mapping
const actionLabels = {
  pin_create: "Pin Oluşturma",
  pin_update: "Pin Güncelleme",
  pin_delete: "Pin Silme",
  pin_bulk_create: "Toplu Pin Oluşturma",
  pin_bulk_delete: "Toplu Pin Silme",
  pin_bulk_update: "Toplu Pin Güncelleme",
  user_delete: "Kullanıcı Silme",
  user_ban: "Kullanıcı Yasaklama",
  user_unban: "Kullanıcı Yasak Kaldırma",
  user_role_change: "Kullanıcı Rol Değişikliği",
  user_update: "Kullanıcı Güncelleme",
  campaign_create: "Kampanya Oluşturma",
  campaign_update: "Kampanya Güncelleme",
  campaign_delete: "Kampanya Silme",
  notification_send: "Bildirim Gönderme",
  notification_send_all: "Toplu Bildirim Gönderme",
  notification_topic_send: "Topic Bildirimi",
  report_review: "Şikayet İnceleme",
  report_dismiss: "Şikayet Reddi",
  report_action: "Şikayet İşlemi",
  cleanup_logs: "Log Temizleme",
  system_config: "Sistem Yapılandırma",
};

export default function AdminLogsPage() {
  const [logs, setLogs] = useState([]);
  const [emailFilter, setEmailFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (emailFilter.trim()) params.set("email", emailFilter.trim());
    if (actionFilter.trim()) params.set("action", actionFilter.trim());
    if (categoryFilter.trim()) params.set("category", categoryFilter.trim());
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
    
    try {
      const resp = await apiCall(`/api/admin/logs?${params.toString()}`, {
        method: "GET",
      });
      setLogs(resp.logs || []);
    } catch (error) {
      console.error("Log yükleme hatası:", error);
      setLogs([]);
    }
    setLoading(false);
  }, [emailFilter, actionFilter, categoryFilter, startDate, endDate]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleDeleteOldLogs = async () => {
    if (!window.confirm("30 günden eski logları silmek istediğinize emin misiniz?")) {
      return;
    }

    try {
      const olderThan = new Date();
      olderThan.setDate(olderThan.getDate() - 30);
      
      await apiCall("/api/admin/logs", {
        method: "DELETE",
        body: JSON.stringify({ olderThan: olderThan.toISOString() }),
      });
      
      alert("Eski loglar başarıyla silindi");
      loadLogs();
    } catch (error) {
      console.error("Log silme hatası:", error);
      alert("Log silme başarısız oldu");
    }
  };

  const exportToCSV = () => {
    const headers = ["Zaman", "Kategori", "Aksiyon", "Kullanıcı", "Detay"];
    const rows = logs.map((log) => [
      formatDate(log.createdAt),
      log.category || "-",
      actionLabels[log.action] || log.action || "-",
      log.email || log.performedBy || "-",
      getMetadataSummary(log),
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `admin_logs_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    try {
      let date;
      if (timestamp.toDate) {
        date = timestamp.toDate();
      } else if (typeof timestamp === "string") {
        date = new Date(timestamp);
      } else if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else {
        return "-";
      }
      if (isNaN(date.getTime())) return "-";
      return date.toLocaleString("tr-TR");
    } catch (e) {
      return "-";
    }
  };

  const getMetadataSummary = (log) => {
    if (log.action === "pin_create") {
      return `Pin: ${log.pinName || log.pinId || "-"}${
        log.campaignId ? `, Kampanya: ${log.campaignId}` : ""
      }`;
    }
    if (log.action === "pin_bulk_delete") {
      return `${log.deleted || 0} silindi, ${log.notFound || 0} bulunamadı, ${
        log.failed || 0
      } hata`;
    }
    if (log.action === "notification_send_all") {
      return `"${log.title}" - ${log.successCount || 0}/${
        log.totalRecipients || 0
      } başarılı`;
    }
    if (log.action === "notification_send") {
      return `"${log.title || "-"}"`;
    }
    if (log.action === "pin_update") {
      return `Pin: ${log.pinId || "-"} - ${log.action || "-"}`;
    }
    return JSON.stringify(log).substring(0, 50) + "...";
  };

  const handleRowClick = (log) => {
    setSelectedLog(log);
    setDetailsOpen(true);
  };

  const paginatedLogs = logs.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5">Admin Logları</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Logları Yenile">
            <IconButton onClick={loadLogs} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="CSV İndir">
            <IconButton onClick={exportToCSV} disabled={logs.length === 0}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="30 Günden Eski Logları Sil">
            <IconButton onClick={handleDeleteOldLogs} color="error">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            size="small"
            label="E-posta"
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <TextField
            size="small"
            label="Aksiyon"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Kategori</InputLabel>
            <Select
              value={categoryFilter}
              label="Kategori"
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="">Tümü</MenuItem>
              <MenuItem value="pin">Pin</MenuItem>
              <MenuItem value="user">Kullanıcı</MenuItem>
              <MenuItem value="campaign">Kampanya</MenuItem>
              <MenuItem value="notification">Bildirim</MenuItem>
              <MenuItem value="moderation">Moderasyon</MenuItem>
              <MenuItem value="system">Sistem</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Başlangıç Tarihi"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180 }}
          />
          <TextField
            size="small"
            label="Bitiş Tarihi"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180 }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={loadLogs}
            disabled={loading}
          >
            {loading ? "Yükleniyor..." : "Filtrele"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setEmailFilter("");
              setActionFilter("");
              setCategoryFilter("");
              setStartDate("");
              setEndDate("");
            }}
          >
            Temizle
          </Button>
        </Box>
      </Paper>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Zaman</TableCell>
              <TableCell>Kategori</TableCell>
              <TableCell>Aksiyon</TableCell>
              <TableCell>Kullanıcı</TableCell>
              <TableCell>Detay</TableCell>
              <TableCell align="center">İşlem</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedLogs.map((log) => (
              <TableRow
                key={log.id}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => handleRowClick(log)}
              >
                <TableCell>{formatDate(log.createdAt)}</TableCell>
                <TableCell>
                  <Chip
                    label={log.category || "system"}
                    color={categoryColors[log.category] || "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {actionLabels[log.action] || log.action || "-"}
                </TableCell>
                <TableCell>{log.email || log.performedBy || "-"}</TableCell>
                <TableCell>{getMetadataSummary(log)}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleRowClick(log)}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {loading ? "Yükleniyor..." : "Kayıt yok"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={logs.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Sayfa başına satır:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} / ${count}`
          }
        />
      </Paper>

      {/* Log Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Log Detayları</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>Tarih:</strong> {formatDate(selectedLog.createdAt)}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Kategori:</strong>{" "}
                <Chip
                  label={selectedLog.category || "system"}
                  color={categoryColors[selectedLog.category] || "default"}
                  size="small"
                />
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Aksiyon:</strong>{" "}
                {actionLabels[selectedLog.action] || selectedLog.action || "-"}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Kullanıcı:</strong>{" "}
                {selectedLog.email || selectedLog.performedBy || "-"}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Başarılı:</strong> {selectedLog.success !== false ? "Evet" : "Hayır"}
              </Typography>
              {selectedLog.error && (
                <Typography variant="body2" sx={{ mt: 1, color: "error.main" }}>
                  <strong>Hata:</strong> {selectedLog.error}
                </Typography>
              )}
              <Typography variant="body2" sx={{ mt: 2 }}>
                <strong>Metadata:</strong>
              </Typography>
              <Paper
                sx={{
                  p: 2,
                  mt: 1,
                  bgcolor: "grey.100",
                  maxHeight: 300,
                  overflow: "auto",
                }}
              >
                <pre style={{ margin: 0, fontSize: "0.85rem" }}>
                  {JSON.stringify(
                    {
                      ...selectedLog,
                      createdAt: undefined,
                      id: undefined,
                      action: undefined,
                      category: undefined,
                      performedBy: undefined,
                      email: undefined,
                      success: undefined,
                      error: undefined,
                    },
                    null,
                    2
                  )}
                </pre>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
